import { Button } from "@ui/components/ui/button";
import { ProductAddedToCartListItem } from "@modules/Navbar/components/ProductAddedToCartListItem";
import { cn } from "@ui/lib/utils";
import { buttonVariants } from "@ui/components/variants/buttonVariants";
import { Link } from "@tanstack/react-router";
import { useLocale } from "@modules/shared/hooks/useLocale";
import { Check, X } from "lucide-react";
import { CartLineProvider, Money, useCart } from "@shopify/hydrogen-react";
import { m } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";

type Props = {
  closeModal: () => void;
  cancelClosing: () => void;
};

export const ProductAddedModal: React.FC<Props> = ({
  closeModal,
  cancelClosing,
}) => {
  const { t } = useTranslation("SingleProductPage");
  const { t: cartTranslate } = useTranslation("Cart");
  const locale = useLocale();
  const { lines, cost } = useCart();

  useEffect(() => {
    closeModal();
  }, [closeModal]);

  const sortedLines = useMemo(
    () => (lines ? [...lines].reverse() : []),
    [lines],
  );

  return (
    <>
      <div
        className={cn(
          "items-center mobile:hidden",
          "absolute left-0 top-[50px] flex w-screen max-w-[100vw] justify-between rounded-[4px] bg-primary-lighter3 px-5 py-4 text-text-1 shadow-[0_12px_24px] shadow-[#0000000f]",
        )}
      >
        <div className="flex items-center gap-4">
          <Check size={16} className="text-primary" aria-hidden="true" />
          {t("productAddedToCart")}
        </div>
        <Button
          type="button"
          className="p-0 text-primary hover:text-primary-darker1"
          light="ghost"
          onClick={() => {
            globalThis.dispatchEvent(new CustomEvent("cart:modalClose"));
          }}
        >
          <X size={16} aria-hidden="true" />
          <span className="sr-only">{t("close")}</span>
        </Button>
      </div>

      <m.div
        className={cn(
          "absolute right-0 top-9 z-[1] w-[290px] rounded-[12px] border border-border-5 bg-white shadow-[0_10px_40px] shadow-[#00000014] max-mobile:hidden",
        )}
        onMouseOver={() => {
          cancelClosing();
        }}
        onMouseLeave={() => {
          closeModal();
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col gap-6 p-4">
          <div className="flex max-h-[290px] flex-col gap-3 overflow-auto">
            {sortedLines.map(
              (line) =>
                line && (
                  <CartLineProvider key={line.id} line={line}>
                    <ProductAddedToCartListItem />
                  </CartLineProvider>
                ),
            )}
          </div>

          <div className="text-1 flex flex-col bg-white">
            <div className="flex justify-between font-primary text-small font-bold -tracking-[0.02em]">
              <p>{cartTranslate("totalVatIncl")}</p>
              <div className="flex flex-col gap-2">
                {cost?.totalAmount && <Money data={cost.totalAmount} />}
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-[0_0_12px_12px] border-t border-border-5 bg-white px-4 py-3">
          <Link
            to="/$locale/cart"
            params={{ locale }}
            className={cn(
              buttonVariants({ primary: "default", size: "small" }),
              "flex w-full justify-center",
            )}
          >
            {cartTranslate("title")}
          </Link>
        </div>
      </m.div>
    </>
  );
};
